import React, { useState, useCallback, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { MultiSelectProps, Switch } from '@tmapy/style-guide';
import {
  BtnBar,
  CloseDialogBtn,
  CtaBtn,
  DangerAlert,
  Form,
  FormRow,
  FormItem,
  Input,
  MultiSelect,
  SecondaryBtn,
  SuccessAlert,
  WaitingAlert,
} from '@tmapy/style-guide';

import type {
  NestedPublishsetResponse,
  PublishsetTagEditRequest,
  PublishsetTagResponse,
} from '../../openapi/types';
import { publishsetsList } from '../../openapi/operations/publishsetsList';
import { FormBlock } from '../../app/components/FormBlock';
import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { QUERY_STATE } from '../../constants';

import type { OmittedFormItemProps } from '../../types';
import type { InputProps } from '../../hooks/useInput';
import type { UseResource } from '../../hooks/useResource';
import { useInput, useAuth, useHistoryAction, useMultiSelect } from '../../hooks';
import { useCheckbox } from '../../hooks/useCheckbox';
import { getFormValues } from '../../docs/utils/getformvalues';

import { TAG_FORM_TYPE } from '../constants';
import {
  tagFormMessages,
  tagFormBtnMessages,
  tagFormFailMessages,
  tagFormQueryMessages,
  tagFormTitleMessages,
} from './tagMessages';

type TagFormProps = {
  formType: TAG_FORM_TYPE;
  resource: UseResource<PublishsetTagResponse>;
  submitQueryState: QUERY_STATE;
  deleteQueryState: QUERY_STATE;
  onSubmit(value: PublishsetTagEditRequest): void;
  onDelete(): void;
  onCloseDialog?(): void;
};

const emptyPublishsets: NestedPublishsetResponse[] = [];

export const TagForm: React.FC<TagFormProps> = ({
  formType,
  resource,
  submitQueryState,
  deleteQueryState,
  onSubmit,
  onDelete,
  onCloseDialog,
}) => {
  const intl = useIntl();
  const auth = useAuth();
  const loadQueryState = resource.queryState;
  const isFormValid = useRef(true);

  const [validate, setValidate] = useState(false);
  const [isFormChanged, setFormChanged] = useState(false);

  const setFormInvalid = (message = 'VALUE_IS_NOT_VALID'): string => {
    isFormValid.current = false;
    return message;
  };

  const onInputChangeCallback = useCallback(() => {
    setFormChanged(true);
    setValidate(false);
  }, [setFormChanged, setValidate]);

  // INPUTS
  const name = useInput<InputProps>({
    name: 'name',
    initialValue: resource.data?.name ?? '',
    label: intl.formatMessage(tagFormMessages.nameLabel),
    validate,
    validator: (value) =>
      value ? null : setFormInvalid(intl.formatMessage(tagFormMessages.nameErrorMessage)),
    isRequired: true,
    autoFocus: formType === TAG_FORM_TYPE.NEW,
    onChange: onInputChangeCallback,
  });

  const is_default = useCheckbox({
    name: 'is_default',
    initialValue: resource.data?.is_default || false,
    label: intl.formatMessage(tagFormMessages.isDefault),
    onChange: onInputChangeCallback,
  });

  const preparePublishsetList = useCallback(async (filter: string) => {
    const publishsetList = await publishsetsList({ query: { name__icontains: filter } });
    return publishsetList.data?.results;
  }, []);

  const publishsets = useMultiSelect<
    MultiSelectProps<NestedPublishsetResponse> | OmittedFormItemProps,
    NestedPublishsetResponse
  >({
    name: 'publishsets',
    initialValue: resource.data?.publishsets ?? emptyPublishsets,
    label: intl.formatMessage(tagFormMessages.publishsetsLabel),
    renderValue: (item) => item.name,
    prepareItems: preparePublishsetList,
    renderItem: (item) => item.name,
    onChange: onInputChangeCallback,
  });

  // POST
  const formItems = {
    name,
    is_default,
    publishsets,
  };

  // HANDLERS
  const handleClose = useHistoryAction('vydejTagList');
  const handleClear = useCallback(() => resource.refetch(), [resource]);
  const handleCancel = useCallback(() => resource.refetch(), [resource]);

  const handleDelete = async () => {
    if (window.confirm(intl.formatMessage(tagFormMessages.tagAlertDelete))) {
      onDelete();
    }
  };

  const handleSubmit = useCallback(() => {
    isFormValid.current = true;
    setValidate(true);
  }, [setValidate]);

  const submit = async () => {
    setValidate(false);
    const values = getFormValues(formItems);
    onSubmit({
      ...values,
      is_default: !!values.is_default,
      publishsets: values.publishsets.map((publishset) => publishset.url),
    });
  };

  // valid form -> proccess SUBMIT
  if (validate && isFormValid.current) {
    setFormChanged(false);
    submit();
  }

  // RENDER
  return (
    <>
      {onCloseDialog && <CloseDialogBtn onClick={onCloseDialog} />}
      <MainContent>
        <article className='sg-u-vs-4'>
          <PageTitle>
            <FormattedMessage {...tagFormTitleMessages[formType]} />
          </PageTitle>
          {loadQueryState === QUERY_STATE.WAITING && (
            <WaitingAlert>
              <FormattedMessage {...tagFormQueryMessages.queryStateWaiting} />
            </WaitingAlert>
          )}
          {loadQueryState === QUERY_STATE.FAIL && (
            <DangerAlert>
              <FormattedMessage {...tagFormQueryMessages.loadQueryStateFail} />
            </DangerAlert>
          )}
          {(loadQueryState === QUERY_STATE.SUCCESS || formType === TAG_FORM_TYPE.NEW) && (
            <Form onSubmit={handleSubmit}>
              <div className='sg-u-vs-2'>
                <FormBlock>
                  <FormRow>
                    <FormItem {...name}>
                      <Input {...name} />
                    </FormItem>
                  </FormRow>
                  <FormRow>
                    <FormItem {...publishsets}>
                      <MultiSelect {...publishsets} />
                    </FormItem>
                  </FormRow>
                  <FormRow>
                    <FormItem {...is_default}>
                      <Switch {...is_default} />
                    </FormItem>
                  </FormRow>
                </FormBlock>

                {submitQueryState === QUERY_STATE.WAITING && (
                  <WaitingAlert>
                    <FormattedMessage {...tagFormQueryMessages.queryStateWaiting} />
                  </WaitingAlert>
                )}
                {submitQueryState === QUERY_STATE.FAIL && (
                  <DangerAlert>
                    <FormattedMessage {...tagFormFailMessages[formType]} />
                  </DangerAlert>
                )}
                {submitQueryState === QUERY_STATE.SUCCESS && (
                  <SuccessAlert>
                    <FormattedMessage {...tagFormQueryMessages.submitQueryStateSuccess} />
                  </SuccessAlert>
                )}

                {deleteQueryState === QUERY_STATE.WAITING && (
                  <WaitingAlert>
                    <FormattedMessage {...tagFormQueryMessages.queryStateWaiting} />
                  </WaitingAlert>
                )}
                {deleteQueryState === QUERY_STATE.FAIL && (
                  <DangerAlert>
                    <FormattedMessage {...tagFormQueryMessages.deleteQueryStateFail} />
                  </DangerAlert>
                )}

                <BtnBar>
                  <>
                    {formType === TAG_FORM_TYPE.EDIT && (
                      <>
                        {isFormChanged ? (
                          <>
                            <CtaBtn type='submit'>
                              <FormattedMessage {...tagFormBtnMessages.save} />
                            </CtaBtn>
                            <SecondaryBtn isDanger onClick={handleCancel}>
                              <FormattedMessage {...tagFormBtnMessages.cancel} />
                            </SecondaryBtn>
                          </>
                        ) : (
                          <CtaBtn onClick={handleClose}>
                            <FormattedMessage {...tagFormBtnMessages.undo} />
                          </CtaBtn>
                        )}
                        {auth.hasPermission('Tags', 'destroy', resource?.data) && (
                          <SecondaryBtn isDanger onClick={handleDelete}>
                            <FormattedMessage {...tagFormBtnMessages.delete} />
                          </SecondaryBtn>
                        )}
                      </>
                    )}
                    {formType === TAG_FORM_TYPE.NEW && (
                      <>
                        <CtaBtn type='submit'>
                          <FormattedMessage {...tagFormBtnMessages.create} />
                        </CtaBtn>
                        {onCloseDialog && (
                          <SecondaryBtn isDanger onClick={onCloseDialog}>
                            <FormattedMessage {...tagFormBtnMessages.close} />
                          </SecondaryBtn>
                        )}
                        <SecondaryBtn isDanger onClick={handleClear}>
                          <FormattedMessage {...tagFormBtnMessages.clear} />
                        </SecondaryBtn>
                      </>
                    )}
                  </>
                </BtnBar>
              </div>
            </Form>
          )}
        </article>
      </MainContent>
    </>
  );
};
